import React, { useState } from 'react';

import { Field } from '@buildoutinc/blueprint-react/ui/Field';
import { Textarea } from '@buildoutinc/blueprint-react/ui/Textarea';

import { Button } from 'components/ui/button';
import { showSnackbar } from 'components/ui/snackbar/helper';
import { PhoneEnrollmentSection } from 'components/ai/phone_enrollment';

import { put } from 'helpers/api';

import type { AiSettings, PhoneEnrollment, UserProfile } from 'components/users/types';

type Props = Pick<UserProfile, 'aiSettings' | 'features' | 'paths' | 'phoneEnrollment'> & {
  smsPhoneNumber?: string | null;
};

const MAX_ASSISTANT_INSTRUCTIONS = 2000;

export default function AiSettingsForm({
  aiSettings,
  features,
  paths,
  phoneEnrollment,
  smsPhoneNumber,
}: Props) {
  const [settings, setSettings] = useState<AiSettings>(aiSettings);
  const [enrollment, setEnrollment] = useState<PhoneEnrollment | null>(phoneEnrollment);
  const [saving, setSaving] = useState(false);

  function handleChange(key: keyof AiSettings, value: string) {
    setSettings(prevSettings => ({ ...prevSettings, [key]: value }));
  }

  async function handleSave() {
    setSaving(true);

    try {
      await put(paths.update, { user: settings });
      showSnackbar({ text: 'Your AI settings were saved.', variant: 'success' });
    } catch {
      showSnackbar({ text: 'Something went wrong saving your AI settings.', variant: 'danger' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <h5 className="mb-4">AI Settings</h5>

      <div className="form-vertical-field-container">
        {features.alSettings && (
          <Field className="form-group">
            <Field.Label htmlFor="aiChatMarketingTone">
              How would you describe the tone of your marketing material?
            </Field.Label>
            <Textarea
              id="aiChatMarketingTone"
              rows={2}
              value={settings.aiChatMarketingTone ?? ''}
              onChange={e => handleChange('aiChatMarketingTone', e.target.value)}
            />
            <Field.Description>
              Examples: conversational, upbeat, friendly, assertive, encouraging, motivating, confident, formative
            </Field.Description>
          </Field>
        )}

        <Field className="form-group">
          <Field.Label htmlFor="aiChatWritingStyle">Do you prefer a specific writing style?</Field.Label>
          <Textarea
            id="aiChatWritingStyle"
            rows={2}
            value={settings.aiChatWritingStyle ?? ''}
            onChange={e => handleChange('aiChatWritingStyle', e.target.value)}
          />
          <Field.Description>
            Examples: informative, enthusiastic, engaging, persuasive, authoritative, casual, formal, concise
          </Field.Description>
        </Field>

        {features.assistantSettings && (
          <Field className="form-group">
            <Field.Label htmlFor="aiAssistantInstructions">
              Anything else the Buildout assistant should know about how you work?
            </Field.Label>
            <Textarea
              id="aiAssistantInstructions"
              maxLength={MAX_ASSISTANT_INSTRUCTIONS}
              rows={4}
              value={settings.aiAssistantInstructions ?? ''}
              onChange={e => handleChange('aiAssistantInstructions', e.target.value)}
            />
            <Field.Description>
              Applies to your chats with the assistant. Example: the markets and property types you cover.
            </Field.Description>
          </Field>
        )}

        {features.assistantSettings && (
          <PhoneEnrollmentSection
            enrollment={enrollment}
            paths={paths}
            personalNumber={smsPhoneNumber || null}
            onChange={setEnrollment}
          />
        )}

        <div className="text-end">
          <Button disabled={saving} label="Save" onClick={handleSave} />
        </div>
      </div>
    </>
  );
}
